/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import {
  css,
  html,
  LitElement,
  ReactiveController,
  ReactiveControllerHost,
} from "lit";
import { customElement, property } from "lit/decorators.js";

import "@material/mwc-list/mwc-list";
import "@material/mwc-list/mwc-check-list-item";
import "@material/mwc-button";
import "@material/mwc-textfield";
import "@material/mwc-linear-progress";

import {
  FileReviewRequest,
  FirestoreReviewRequest,
  PubSubReviewRequest,
  ReviewRequest,
} from "./review-request";
import { EntityLabel } from "./entity-label";
import { GroupingLabel } from "./grouping-label";
import {
  PROCESSOR_TO_REVIEW_TYPE_MAP,
  ReviewRequestType,
} from "./processor-types";
import { ClassificationLabel } from "./classification-label";
import { fetchRetry } from "./fetch-retry";
import { getQueueDisplayName } from "./mapping";
import { log } from "./logger";
import { SessionManager } from "./session-manager";

// Firestore interfaces for Firestore queue type
import { Timestamp } from "firebase/firestore";
import { QueueProviders, type QueueType } from "../types/queues";

// Queue interface for UI
export interface Queue {
  name: string;
  displayName: string;
  reviewRequestCount: number;
  activeCount?: number;
  assignedCount?: number;
  completedToday?: number;
  hasMoreDocuments?: boolean;
  getReviewRequests?: (count: number) => Promise<ReviewRequest[]>;
}

// Firestore interfaces
export interface QueueInfo {
  queueId: string;
  displayName: string;
  activeCount: number;
  assignedCount: number;
  completedToday: number;
}

export interface HitlTask {
  taskId: string;
  documentId: string;
  envelopeId: string;
  queueName: string;
  processorType: string;
  priority: number;
  createdAt: Timestamp;
  status: "available" | "assigned" | "in_progress" | "completed" | "escalated";
  assignedTo?: string;
  assignedAt?: Timestamp;
  expiresAt?: Timestamp;
  source?: string;
  callbackUrl?: string;
  documentData: {
    pageCount: number;
    pages: Array<{
      pageNumber: number;
      previewUrl: string;
      fullUrl: string;
      dimensions: { width: number; height: number };
      fileSizes: { preview: number; full: number };
      estimatedLoadTime: number;
    }>;
    loadingMetrics: {
      totalPreviewSize: number;
      estimatedLoadTime: number;
      recommendedPreload: number;
    };
    resultBucket: string;
    resultPrefix: string;
    resultObject: string;
    labels: any[];
    metadata: Record<string, any>;
  };
}

@customElement("hitl-queues")
export class Queues extends LitElement {
  private messageWorker: Worker | null = null;
  private firestoreUnsubscribers: Map<string, () => void> = new Map();

  // Local tracking for immediate UI feedback before server confirmation
  private localQueueAdjustments = new Map<
    string,
    { availableAdjustment: number; assignedAdjustment: number }
  >();

  // Adaptive polling for better performance
  private lastUserActivity: number = Date.now();
  private currentPollingInterval: number = 30000; // Start with 30 seconds
  private pollingTimer: number | null = null;

  // Change detection for requestUpdate optimization
  private lastQueueCountsHash: string = "";
  private pendingUpdateRequest: boolean = false;

  // Timeout for local adjustments
  private adjustmentTimeouts = new Map<string, number>();

  // Simple multi-user coordination
  static override styles = css`
    .mdc-card > div {
      margin: 0px 12px 0 12px;
      --mdc-theme-primary: #be153f;
      --mdc-theme-on-primary: white;
    }

    .mdc-card__actions {
      gap: 12px;
    }
  `;

  @property({ type: String })
  queueType: QueueType = "Firestore";
  @property({ type: Array })
  queues: Queue[] = [];
  @property({ type: Number })
  reviewRequestsPerQueue: number = 10;

  queueSelection: number[] = [];

  @property({ type: Boolean })
  loading: boolean = false;

  @property({ type: String })
  envelopeIdSearch: string = "";

  private loadingPromise: Promise<void> | null = null;

  projectId?: string = process.env.PROJECT_ID;
  clientPrefix?: string = process.env.CLIENT_PREFIX || "devk";

  @property({ type: String })
  environment: string = "";

  constructor() {
    super();
    // Initialize Web Worker for message processing (optional optimization)
    if (typeof Worker !== "undefined") {
      try {
        this.messageWorker = new Worker(
          new URL("./message-processor.worker.ts", import.meta.url),
          {
            type: "module",
          },
        );
      } catch (error) {
        console.warn(
          "Failed to initialize Web Worker, falling back to main thread processing:",
          error,
        );
      }
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    // Initialize environment detection after properties are set
    if (this.queueType === QueueProviders.Firestore) {
      if (!this.projectId) {
        throw "Project ID not provided";
      }
      // console.log(`CLIENT_PREFIX: '${process.env.CLIENT_PREFIX}'`);
      // console.log(`ENVIRONMENT: '${process.env.ENVIRONMENT}'`);
      this.environment =
        process.env.CLIENT_PREFIX + "-" + process.env.ENVIRONMENT;
      // console.log(`Environment set to: '${this.environment}'`);
      // console.log(`Expected topic name would be: '${this.environment}-hitl-logger'`);

      // Initialize Firestore connection
      this.initializeFirestore();

      // Emit environment information for logger configuration
      if (this.environment) {
        const environmentEvent = new CustomEvent("environment-detected", {
          bubbles: true,
          detail: { environment: this.environment },
        });
        this.dispatchEvent(environmentEvent);
      }
    }
  }

  private async initializeFirestore() {
    // Using REST API instead of Firebase SDK for named database access
    console.log(
      `Firestore REST API configured for database: ${this.environment}-hitl-tasks`,
    );
  }

  private async subscribeToFirestoreQueues() {
    const queueNames = this.queues.map((q) => q.name);
    console.log("Setting up polling for Firestore queues:", queueNames);

    // Skip subscription if no queues are available
    if (queueNames.length === 0) {
      console.log("No queues available for subscription");
      return;
    }

    // Use polling with activity monitoring for cross-user coordination
    const pollQueues = async () => {
      try {
        const [accessToken, idToken] = await this.getAccessToken(false);
        const databaseId = `${this.environment}-hitl-tasks`;

        // Update counts for all queues by counting actual documents in task collections
        await this.updateQueueCountsWithAPI(
          this.queues,
          accessToken,
          idToken,
          databaseId,
        );

        // Request UI update after counts are updated (optimized)
        this.requestUpdateIfChanged();
      } catch (error) {
        console.error("Queue polling error:", error);
      }
    };

    // Initial poll
    await pollQueues();

    // Set up simplified polling optimized for multi-user scenarios
    this.startSimpleRealtimePolling(pollQueues);
  }

  /**
   * Handle task release by clearing local adjustments and refreshing counts
   * Called when documents are removed from work stack
   */
  handleTaskRelease(queueName: string, taskCount: number = 1): void {
    console.log(
      `🔄 Handling task release for queue ${queueName}: ${taskCount} tasks`,
    );

    // Apply positive adjustment to show documents are available again
    this.applyLocalQueueAdjustment(queueName, taskCount, 0);

    // Force immediate queue refresh to get updated server counts
    this.refreshQueueCountsImmediate().catch((error) =>
      console.warn("Failed to refresh after task release:", error),
    );
  }

  /**
   * Immediately refresh queue counts for real-time UI updates
   * Called after document assignment, release, or completion
   */
  async refreshQueueCountsImmediate(): Promise<void> {
    try {
      console.log("🔄 Performing immediate queue count refresh...");
      const [accessToken, idToken] = await this.getAccessToken(false);
      const databaseId = `${this.environment}-hitl-tasks`;

      // Store current local adjustments before refresh
      const currentAdjustments = new Map(this.localQueueAdjustments);

      // Update counts for all queues immediately using the existing counting logic
      await this.updateQueueCountsWithAPI(
        this.queues,
        accessToken,
        idToken,
        databaseId,
      );

      // For pre-assigned documents, server counts won't change immediately
      // Keep local adjustments to show correct UI state to users
      // Only refresh adjustments when tasks are actually released/completed
      console.log(
        "⏳ Preserving local adjustments - server counts may not reflect document loading for pre-assigned tasks",
      );
      this.localQueueAdjustments = currentAdjustments;

      // Force UI update (optimized)
      this.requestUpdateIfChanged();
      console.log("✅ Immediate queue count refresh completed");
    } catch (error) {
      console.error("❌ Failed to refresh queue counts immediately:", error);
    }
  }

  /**
   * Apply local adjustment to queue counts for instant UI feedback
   */
  private applyLocalQueueAdjustment(
    queueName: string,
    availableChange: number,
    assignedChange: number,
  ): void {
    const current = this.localQueueAdjustments.get(queueName) || {
      availableAdjustment: 0,
      assignedAdjustment: 0,
    };
    this.localQueueAdjustments.set(queueName, {
      availableAdjustment: current.availableAdjustment + availableChange,
      assignedAdjustment: current.assignedAdjustment + assignedChange,
    });

    console.log(
      `📊 Local adjustment for ${queueName}: available ${availableChange > 0 ? "+" : ""}${availableChange}, assigned ${assignedChange > 0 ? "+" : ""}${assignedChange}`,
    );

    // Clear any existing timeout for this queue
    const existingTimeout = this.adjustmentTimeouts.get(queueName);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set timeout to clear adjustment after 2 minutes for very stale adjustments
    // This is a safety net - normal clearing should happen on task release/completion
    const timeoutId = window.setTimeout(() => {
      console.log(
        `⏰ Safety timeout: clearing very stale local adjustment for ${queueName}`,
      );
      this.localQueueAdjustments.delete(queueName);
      this.adjustmentTimeouts.delete(queueName);
      this.requestUpdate();
    }, 120000); // 2 minutes instead of 30 seconds

    this.adjustmentTimeouts.set(queueName, timeoutId);

    // Force UI update to show local changes immediately
    this.requestUpdate();
  }

  /**
   * Get effective queue count including local adjustments
   */
  private getEffectiveQueueCount(
    queue: Queue,
    countType: "available" | "assigned",
  ): number {
    const adjustment = this.localQueueAdjustments.get(queue.name);
    const baseCount =
      countType === "available"
        ? queue.reviewRequestCount || 0
        : queue.assignedCount || 0;

    if (!adjustment) {
      return baseCount;
    }

    const adjustmentValue =
      countType === "available"
        ? adjustment.availableAdjustment
        : adjustment.assignedAdjustment;
    const effectiveCount = Math.max(0, baseCount + adjustmentValue);

    // Only log when there's an actual adjustment being applied
    if (adjustmentValue !== 0) {
      console.log(
        `📊 Queue ${queue.name} ${countType}: base=${baseCount}, adjustment=${adjustmentValue}, effective=${effectiveCount}`,
      );
    }

    // Ensure we don't show negative counts
    return effectiveCount;
  }

  /**
   * Record user activity to optimize polling frequency
   */
  private recordUserActivity(): void {
    this.lastUserActivity = Date.now();
  }

  /**
   * Simple real-time polling optimized for multi-user coordination
   * Polls every 10 seconds when users are active, 30 seconds when idle
   */
  private startSimpleRealtimePolling(pollFunction: () => Promise<void>): void {
    let pollCount = 0;

    const performPoll = async () => {
      try {
        await pollFunction();
        pollCount++;

        // Determine next interval based on activity
        const timeSinceActivity = Date.now() - this.lastUserActivity;
        let nextInterval: number;

        if (timeSinceActivity < 2 * 60 * 1000) {
          // Active user: poll every 10 seconds for quick updates
          nextInterval = 10000;
        } else if (timeSinceActivity < 10 * 60 * 1000) {
          // Moderately idle: poll every 20 seconds
          nextInterval = 20000;
        } else {
          // Idle: poll every 30 seconds
          nextInterval = 30000;
        }

        // Only log polling progress every 10 polls to reduce console noise
        if (pollCount % 10 === 0) {
          console.log(
            `📊 Queue poll #${pollCount} completed, next poll in ${nextInterval / 1000}s`,
          );
        }

        // Schedule next poll
        const timeoutId = setTimeout(performPoll, nextInterval);

        // Store cleanup function
        this.firestoreUnsubscribers.set("queue-polling", () =>
          clearTimeout(timeoutId),
        );
      } catch (error) {
        console.error("Polling error:", error);
        // Retry in 30 seconds on error
        const retryTimeoutId = setTimeout(performPoll, 30000);
        this.firestoreUnsubscribers.set("queue-polling", () =>
          clearTimeout(retryTimeoutId),
        );
      }
    };

    // Start the polling cycle
    console.log(
      "🔄 Starting simplified real-time polling (10-30 second intervals)",
    );
    performPoll();
  }

  // Legacy Firebase SDK method - replaced with REST API version
  // private async fetchFirestoreTaskBatch(queueName: string, batchSize: number = 10): Promise<HitlTask[]> {
  //   if (!this.firestoreDb) {
  //     console.error('Firestore not initialized');
  //     return [];
  //   }

  //   console.log('Fetching task batch from Firestore:', { queueName, batchSize });

  //   const q = query(
  //     collection(this.firestoreDb, 'hitl_tasks'),
  //     where('queueName', '==', queueName),
  //     where('status', '==', 'available'),
  //     orderBy('priority', 'desc'),
  //     orderBy('createdAt', 'asc'),
  //     limit(batchSize)
  //   );

  //   const snapshot = await getDocs(q);
  //   return snapshot.docs.map(doc => ({
  //     taskId: doc.id,
  //     ...doc.data()
  //   } as HitlTask));
  // }

  private async assignFirestoreTask(
    taskId: string,
    userId: string,
  ): Promise<boolean> {
    if (!this.firestoreDb) {
      console.error("Firestore not initialized");
      return false;
    }

    console.log("Assigning Firestore task:", taskId);

    const taskRef = doc(this.firestoreDb, "hitl_tasks", taskId);
    const sessionRef = doc(this.firestoreDb, "hitl_user_sessions", userId);

    try {
      const result = await runTransaction(
        this.firestoreDb,
        async (transaction) => {
          const taskDoc = await transaction.get(taskRef);

          if (!taskDoc.exists() || taskDoc.data()?.status !== "available") {
            throw new Error("Task no longer available");
          }

          const taskData = taskDoc.data();
          const expiresAt = Timestamp.fromDate(
            new Date(Date.now() + 60 * 60 * 1000), // 1 hour
          );

          // Update task
          transaction.update(taskRef, {
            status: "assigned",
            assignedTo: userId,
            assignedAt: serverTimestamp(),
            expiresAt: expiresAt,
            viewCount: increment(1),
          });

          // Update user session
          transaction.set(
            sessionRef,
            {
              userId: userId,
              activeTaskIds: arrayUnion(taskId),
              lastActivity: serverTimestamp(),
            },
            { merge: true },
          );

          // Update queue stats
          const queueRef = doc(
            this.firestoreDb,
            "hitl_queues",
            taskData.queueName,
          );
          transaction.update(queueRef, {
            activeTaskCount: increment(-1),
            assignedTaskCount: increment(1),
          });

          return true;
        },
      );

      console.log("Successfully assigned Firestore task:", taskId);
      return result;
    } catch (error) {
      console.error("Failed to assign Firestore task:", error);
      return false;
    }
  }

  private async getFirestoreQueues(): Promise<Queue[]> {
    try {
      // Query Firestore to discover available queues using REST API with access token
      console.log(
        "Discovering available queues from Firestore using REST API...",
      );

      // Get access token for authentication
      const [accessToken, idToken] = await this.getAccessToken(false);

      const databaseId = `${this.environment}-hitl-tasks`;
      const url = `${process.env.INTERNAL_API_URL}/documents/hitl_queues`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch queues: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      const discoveredQueues: Queue[] = [];

      if (data.documents) {
        data.documents.forEach((doc: any) => {
          // Extract queue name from document path
          const queueName = doc.document_id;

          // Extract displayName from document fields, then apply mapping
          const firestoreDisplayName = doc.displayName || queueName;
          const displayName = getQueueDisplayName(queueName);

          console.log(`Found queue: ${queueName} (display: ${displayName})`);

          discoveredQueues.push({
            name: queueName,
            displayName: displayName,
            reviewRequestCount: 0, // Will be calculated dynamically
            activeCount: 0,
            assignedCount: 0,
            completedToday: 0,
            hasMoreDocuments: false,
            getReviewRequests: async (count: number) => {
              try {
                return await this.getFirestoreReviewRequests(queueName, count);
              } catch (error) {
                console.error(
                  `Failed to get review requests for queue ${queueName}:`,
                  error,
                );
                return [];
              }
            },
          });
        });
      }

      // Sort queues by display name for consistent ordering
      discoveredQueues.sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      );

      // Update queue counts dynamically by counting actual tasks
      await this.updateQueueCountsWithAPI(
        discoveredQueues,
        accessToken,
        idToken,
        databaseId,
      );

      console.log(
        `Discovered ${discoveredQueues.length} queues from Firestore`,
      );
      return discoveredQueues;
    } catch (error) {
      console.error("Failed to discover queues from Firestore:", error);
      // Fallback to empty array if discovery fails
      return [];
    }
  }

  private async updateQueueCountsWithAPI(
    queues: Queue[],
    accessToken: string,
    idToken: string,
    databaseId: string,
  ): Promise<void> {
    try {
      // Track previous counts for change detection
      const previousCounts = new Map<string, number>();
      queues.forEach((q) =>
        previousCounts.set(q.name, q.reviewRequestCount || 0),
      );

      // Count tasks for each queue using BULK REST API optimization
      const bulkCounts = await this.bulkCountFirestoreTasks(
        queues.map((q) => q.name),
        accessToken,
        idToken,
        databaseId,
      );

      await Promise.all(
        queues.map(async (queue) => {
          try {
            // Get counts from bulk response instead of individual API calls
            const availableCount = bulkCounts[queue.name]?.available || 0;
            const assignedCount = bulkCounts[queue.name]?.assigned || 0;
            const hasMore = bulkCounts[queue.name]?.hasMore || false;

            // The "available" count should represent documents available for assignment
            // This means documents that are not assigned to anyone
            const oldCount = queue.reviewRequestCount || 0;
            queue.reviewRequestCount = availableCount;
            queue.activeCount = availableCount;
            queue.assignedCount = assignedCount;
            queue.hasMoreDocuments = hasMore;

            // Log changes
            if (oldCount !== availableCount) {
              console.log(
                `📬 Queue ${queue.name}: available ${oldCount} → ${availableCount}${hasMore ? "+" : ""} (${availableCount > oldCount ? "🆕 NEW DOCUMENTS" : "documents removed"})`,
              );
            }
            // console.log(`Queue ${queue.name}: ${availableCount} available, ${assignedCount} assigned (total unprocessed: ${availableCount + assignedCount})`);
          } catch (error) {
            console.warn(
              `Failed to count tasks for queue ${queue.name}:`,
              error,
            );
          }
        }),
      );
    } catch (error) {
      console.error("Failed to update queue counts:", error);
    }
  }

  private async countFirestoreTasks(
    queueName: string,
    status: string,
    accessToken: string,
    idToken: string,
    databaseId: string,
  ): Promise<number> {
    try {
      // Use queue-specific collection
      const collectionName = `hitl_tasks_${queueName}`;

      console.log(
        `🔍 Querying Firestore for ${queueName} tasks with status="${status}"`,
      );

      // Use fast count endpoint instead of fetching documents
      const url = `${process.env.INTERNAL_API_URL}/documents/${collectionName}/count?status=${status}`;

      console.log(`📄 Fetching count with URL: ${url}`);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Firestore API error for ${queueName}:`, {
          status: response.status,
          statusText: response.statusText,
          errorText,
          url,
        });
        throw new Error(
          `Failed to count tasks: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();
      const count = data.count || 0;
      const hasMore = data.has_more || false;

      console.log(`📊 Firestore count response for ${queueName}:`, {
        count,
        hasMore,
        limit: data.limit || 1000,
      });

      if (hasMore) {
        console.log(
          `✅ Final count for ${queueName} with status="${status}": ${count}+ documents (limit reached)`,
        );
      } else {
        console.log(
          `✅ Final count for ${queueName} with status="${status}": ${count} documents`,
        );
      }

      return count;
    } catch (error) {
      console.error(
        `Failed to count tasks for queue ${queueName} with status ${status}:`,
        error,
      );
      return 0;
    }
  }

  private async bulkCountFirestoreTasks(
    queueNames: string[],
    accessToken: string,
    idToken: string,
    databaseId: string,
  ): Promise<{
    [queueName: string]: {
      available: number;
      assigned: number;
      hasMore: boolean;
    };
  }> {
    console.log(
      `BULK OPTIMIZATION: Counting ${queueNames.length} queues in parallel`,
    );
    const startTime = performance.now();

    try {
      // Create parallel requests for all queue collections
      const bulkPromises = queueNames.flatMap((queueName) => [
        // Available count request
        {
          queueName,
          status: "available",
          promise: this.countSingleQueueBulk(
            queueName,
            "available",
            accessToken,
            idToken,
            databaseId,
          ),
        },
        // Assigned count request
        {
          queueName,
          status: "assigned",
          promise: this.countSingleQueueBulk(
            queueName,
            "assigned",
            accessToken,
            idToken,
            databaseId,
          ),
        },
      ]);

      // Execute all requests in parallel
      const results = await Promise.allSettled(
        bulkPromises.map((req) => req.promise),
      );

      // Process results into structured format
      const bulkCounts: {
        [queueName: string]: {
          available: number;
          assigned: number;
          hasMore: boolean;
        };
      } = {};

      // Initialize all queues - optimized for loop
      for (let i = 0; i < queueNames.length; i++) {
        bulkCounts[queueNames[i]] = {
          available: 0,
          assigned: 0,
          hasMore: false,
        };
      }

      // Populate results - optimized for loop
      for (let index = 0; index < bulkPromises.length; index++) {
        const req = bulkPromises[index];
        const result = results[index];
        if (result.status === "fulfilled") {
          bulkCounts[req.queueName][req.status as "available" | "assigned"] =
            result.value.count;
          // Track hasMore if either available or assigned has more documents
          if (result.value.hasMore) {
            bulkCounts[req.queueName].hasMore = true;
          }
        } else {
          console.warn(
            `Failed to count ${req.status} tasks for queue ${req.queueName}:`,
            result.reason,
          );
        }
      }

      const totalTime = performance.now() - startTime;
      const totalRequests = queueNames.length * 2;
      console.log(
        `⚡ BULK OPTIMIZATION COMPLETE: ${totalRequests} requests in ${Math.round(totalTime)}ms (${Math.round(totalTime / totalRequests)}ms avg per request)`,
      );

      return bulkCounts;
    } catch (error) {
      console.error(
        "Bulk count operation failed, falling back to individual requests:",
        error,
      );

      // Fallback to individual requests if bulk fails
      const fallbackCounts: {
        [queueName: string]: {
          available: number;
          assigned: number;
          hasMore: boolean;
        };
      } = {};
      await Promise.all(
        queueNames.map(async (queueName) => {
          try {
            const available = await this.countFirestoreTasks(
              queueName,
              "available",
              accessToken,
              idToken,
              databaseId,
            );
            const assigned = await this.countFirestoreTasks(
              queueName,
              "assigned",
              accessToken,
              idToken,
              databaseId,
            );
            fallbackCounts[queueName] = { available, assigned, hasMore: false };
          } catch (err) {
            console.error(`Fallback count failed for queue ${queueName}:`, err);
            fallbackCounts[queueName] = {
              available: 0,
              assigned: 0,
              hasMore: false,
            };
          }
        }),
      );

      return fallbackCounts;
    }
  }

  private async countSingleQueueBulk(
    queueName: string,
    status: string,
    accessToken: string,
    idToken: string,
    databaseId: string,
  ): Promise<{ count: number; hasMore: boolean }> {
    // Fast count using dedicated count endpoint (no document transfer)
    const collectionName = `hitl_tasks_${queueName}`;

    const url = `${process.env.INTERNAL_API_URL}/documents/${collectionName}/count?status=${status}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${idToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to count tasks for ${queueName}: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();

    const count = data.count || 0;
    const hasMore = data.has_more || false;

    // Log the count
    if (hasMore) {
      log.debug(
        `📊 BULK: ${count}+ documents with status="${status}" for ${queueName} (limit reached)`,
      );
    } else {
      log.debug(
        `📊 BULK: ${count} documents with status="${status}" for ${queueName}`,
      );
    }

    return { count, hasMore };
  }

  private async getFirestoreReviewRequests(
    queueName: string,
    count: number,
  ): Promise<ReviewRequest[]> {
    const userId = this.getCurrentUserId();
    const result = await this.fetchAndAssignFirestoreTasksREST(
      queueName,
      count,
      userId,
    );

    // Handle both old array format (fallback) and new object format
    if (Array.isArray(result)) {
      return result.map(
        (task) => new FirestoreReviewRequest(task, null, userId),
      );
    }

    const { tasks, newAssignmentCount, totalCount } = result;

    // Apply immediate local adjustment for user feedback, only for newly assigned tasks
    if (newAssignmentCount > 0) {
      console.log(
        `📊 Applying local adjustment for ${queueName}: -${newAssignmentCount} available (newly assigned documents)`,
      );
      this.applyLocalQueueAdjustment(queueName, -newAssignmentCount, 0);
    }

    return tasks.map((task) => new FirestoreReviewRequest(task, null, userId)); // Pass null for firestoreDb since we're using REST API
  }

  private getCurrentUserId(): string {
    // Get user ID from the parent component or system
    // This should be set when the user logs in
    const hitlApp = document.querySelector("hitl-app") as any;
    if (hitlApp && hitlApp.userId) {
      return hitlApp.userId;
    }

    // Fallback to generating a session-based ID if no user ID is available
    if (!this.sessionUserId) {
      this.sessionUserId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.warn(`No userId found, using session ID: ${this.sessionUserId}`);
    }
    return this.sessionUserId;
  }

  private sessionUserId?: string;

  private async fetchAndAssignFirestoreTasksREST(
    queueName: string,
    batchSize: number = 10,
    userId: string,
  ): Promise<HitlTask[]> {
    try {
      console.log("Fetching task batch from Firestore using REST API:", {
        queueName,
        batchSize,
        userId,
      });

      // Get access token for authentication
      const [accessToken, idToken] = await this.getAccessToken(false);
      const databaseId = `${this.environment}-hitl-tasks`;

      // Use queue-specific collection
      const collectionName = `hitl_tasks_${queueName}`;
      // Build URL - use search endpoint if envelope_id filter is provided
      let url: string;
      if (this.envelopeIdSearch) {
        // Apply FIFO ordering: oldest documents first
        url = `${process.env.INTERNAL_API_URL}/documents/${collectionName}/search?envelope_id=${encodeURIComponent(this.envelopeIdSearch)}&limit=1000&orderBy=createdAt:asc`;
        console.log(`Searching tasks by envelope_id: ${this.envelopeIdSearch}`);
      } else {
        // Apply FIFO ordering: oldest documents first
        url = `${process.env.INTERNAL_API_URL}/documents/${collectionName}?limit=1000&orderBy=createdAt:asc`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${idToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`API Error Response:`, {
          status: response.status,
          statusText: response.statusText,
          url,
          errorText,
        });
        throw new Error(
          `Failed to fetch tasks: ${response.status} ${response.statusText}`,
        );
      }

      const responseText = await response.text();
      console.log(
        `Raw API Response (first 500 chars):`,
        responseText.substring(0, 500),
      );

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error(
          `Failed to parse JSON response:`,
          e,
          responseText.substring(0, 500),
        );
        throw new Error(`Invalid JSON response from API`);
      }

      if (!data || !data.documents) {
        console.log(
          `No documents found in collection for queue ${queueName}`,
          data,
        );
        return [];
      }

      console.log(
        `Total documents found in queue ${queueName}: ${data.documents.length}${data.has_more ? "+" : ""} (limit: ${data.limit || 1000})`,
      );

      // Filter documents for: 1) available tasks, 2) tasks already assigned to current user, 3) not older than 30 days
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const availableDocs = data.documents.filter((doc: any) => {
        const taskStatus = doc.status;
        const assignedTo = doc.assignedTo;
        const createdAt = doc.createdAt;

        // Skip tasks older than 30 days
        if (createdAt && createdAt < thirtyDaysAgo) {
          return false;
        }

        return (
          taskStatus === "available" ||
          (taskStatus === "assigned" && assignedTo === userId)
        );
      });

      // Separate already assigned tasks from available tasks
      const alreadyAssignedDocs = availableDocs.filter((doc: any) => {
        const taskStatus = doc.status;
        const assignedTo = doc.assignedTo;
        return taskStatus === "assigned" && assignedTo === userId;
      });

      const availableForAssignment = availableDocs
        .filter((doc: any) => {
          const taskStatus = doc.status;
          return taskStatus === "available";
        })
        .slice(0, Math.max(0, batchSize - alreadyAssignedDocs.length)); // Reserve space for already assigned

      // Limit already assigned tasks to fit within batch size
      const limitedAlreadyAssigned = alreadyAssignedDocs.slice(0, batchSize);

      console.log(
        `Found ${availableForAssignment.length} available tasks and ${limitedAlreadyAssigned.length} already assigned tasks (limited from ${alreadyAssignedDocs.length}) for queue ${queueName}`,
      );

      // Atomically assign tasks to this user
      const assignedTasks: HitlTask[] = [];
      const assignmentTime = new Date().toISOString();
      const expirationTime = new Date(
        Date.now() + 30 * 60 * 1000,
      ).toISOString(); // 30 minutes from now

      // Track how many new assignments we actually make
      let newAssignmentCount = 0;

      // First, process already assigned tasks (update expiration time to prevent indefinite assignment)
      for (const doc of limitedAlreadyAssigned) {
        try {
          const taskId = doc.taskId;
          console.log(`Using already assigned task: ${taskId}`);

          // Check if task has expiresAt field
          const currentExpiresAt = doc.expiresAt;

          // If no expiration or expired, update it
          if (!currentExpiresAt || new Date(currentExpiresAt) < new Date()) {
            console.log(
              `Updating expiration for already assigned task ${taskId}`,
            );
            const taskDocUrl = `${process.env.INTERNAL_API_URL}/documents/${collectionName}/${taskId}`;

            const updatePayload = {
              expiresAt: expirationTime,
            };

            try {
              const updateResponse = await fetch(taskDocUrl, {
                method: "PATCH",
                headers: {
                  Authorization: `Bearer ${idToken}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify(updatePayload),
              });

              if (updateResponse.ok) {
                console.log(`Updated expiration for task ${taskId}`);
              } else {
                console.warn(
                  `Failed to update expiration for task ${taskId}: ${updateResponse.status}`,
                );
              }
            } catch (updateError) {
              console.warn(
                `Error updating expiration for task ${taskId}:`,
                updateError,
              );
            }
          }

          const task: HitlTask = {
            taskId,
            documentId: doc.documentId || "",
            envelopeId: doc.envelopeId || "",
            queueName: doc.queueName || queueName,
            processorType: doc.processorType || "",
            priority: parseInt(doc.priority || "0"),
            createdAt: doc.createdAt
              ? { toDate: () => new Date(doc.createdAt) }
              : { toDate: () => new Date() },
            documentData: this.parseDocumentData(doc.documentData),
            revisions: doc.revisions?.values || [],
            source: doc.source || undefined,
            callbackUrl: doc.callbackUrl || undefined,
          };
          assignedTasks.push(task);
          console.log(`Using already assigned task: ${taskId}`);
        } catch (error) {
          console.warn(`Failed to process already assigned task:`, error);
        }
      }

      // Then, process available tasks for assignment
      for (const doc of availableForAssignment) {
        try {
          const taskId = doc.taskId;
          const taskDocUrl = `${process.env.INTERNAL_API_URL}/documents/${collectionName}/${taskId}`;

          // Prepare assignment update
          const updatePayload = {
            status: "assigned",
            assignedTo: userId,
            assignedAt: assignmentTime,
            expiresAt: expirationTime,
          };

          // Attempt to assign the task using conditional update
          const updateResponse = await fetch(taskDocUrl, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatePayload),
          });

          if (updateResponse.ok) {
            // Successfully assigned, parse and add to result
            const task: HitlTask = {
              taskId,
              documentId: doc.documentId || "",
              envelopeId: doc.envelopeId || "",
              queueName: doc.queueName || queueName,
              processorType: doc.processorType || "",
              priority: parseInt(doc.priority || "0"),
              createdAt: doc.createdAt
                ? { toDate: () => new Date(doc.createdAt) }
                : { toDate: () => new Date() },
              status: "assigned",
              assignedTo: userId,
              assignedAt: { toDate: () => new Date(assignmentTime) },
              expiresAt: { toDate: () => new Date(expirationTime) },
              documentData: this.parseDocumentData(doc.documentData),
              source: doc.source || undefined,
              callbackUrl: doc.callbackUrl || undefined,
            };
            assignedTasks.push(task);
            newAssignmentCount++; // Track new assignments
            console.log(
              `Successfully assigned NEW task ${taskId} to user ${userId}`,
            );
          } else {
            // Assignment failed (likely race condition), skip this task
            console.log(
              `Failed to assign task ${taskId}: ${updateResponse.status} ${updateResponse.statusText}`,
            );
          }
        } catch (error) {
          console.error(`Error assigning task:`, error);
          // Continue with next task
        }

        // Stop if we've successfully assigned enough tasks
        if (assignedTasks.length >= batchSize) {
          break;
        }
      }

      console.log(
        `Successfully assigned ${assignedTasks.length} tasks for queue ${queueName} to user ${userId} (${newAssignmentCount} new, ${assignedTasks.length - newAssignmentCount} already assigned)`,
      );

      // Return info about both total tasks and new assignments for UI adjustment logic
      return {
        tasks: assignedTasks,
        newAssignmentCount,
        totalCount: assignedTasks.length,
      };
    } catch (error) {
      console.error(
        "Failed to fetch Firestore task batch using REST API:",
        error,
      );
      return { tasks: [], newAssignmentCount: 0, totalCount: 0 };
    }
  }

  /**
   * Release tasks assigned to a specific user (e.g., when user abandons work)
   */
  async releaseUserTasks(userId: string, queueName?: string): Promise<number> {
    try {
      console.log(
        `Releasing tasks for user ${userId}${queueName ? ` in queue ${queueName}` : ""}`,
      );

      const [accessToken, idToken] = await this.getAccessToken(false);
      const databaseId = `${this.environment}-hitl-tasks`;
      let releasedCount = 0;

      // If specific queue, release only that queue's tasks
      const queuesToCheck = queueName
        ? [queueName]
        : this.queues.map((q) => q.name);

      for (const queue of queuesToCheck) {
        const collectionName = `hitl_tasks_${queue}`;
        const baseUrl = `${process.env.INTERNAL_API_URL}/documents/${collectionName}`;
        const queryUrl = `${baseUrl}?status=assigned`;

        // Find assigned tasks for this user
        const response = await fetch(queryUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) continue;

        const data = await response.json();
        if (!data.documents) continue;

        // Filter tasks assigned to this user
        const userTasks = data.documents.filter((doc: any) => {
          const status = doc.status;
          const assignedTo = doc.assignedTo;
          return status === "assigned" && assignedTo === userId;
        });

        // Release each task
        for (const doc of userTasks) {
          const taskId = doc.document_id;
          if (!taskId) continue;

          const taskDocUrl = `${baseUrl}/${taskId}`;
          const updatePayload = {
            status: "available",
            assignedTo: null,
            assignedAt: null,
            expiresAt: null,
          };

          const updateResponse = await fetch(taskDocUrl, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updatePayload),
          });

          if (updateResponse.ok) {
            releasedCount++;
            console.log(`Released task ${taskId} from user ${userId}`);
          }
        }
      }

      console.log(`Released ${releasedCount} tasks for user ${userId}`);
      return releasedCount;
    } catch (error) {
      console.error("Failed to release user tasks:", error);
      return 0;
    }
  }

  /**
   * Auto-release expired tasks across all queues
   */
  async releaseExpiredTasks(): Promise<number> {
    try {
      console.log("Checking for expired task assignments...");

      const [accessToken, idToken] = await this.getAccessToken(false);
      const databaseId = `${this.environment}-hitl-tasks`;
      const currentTime = new Date().toISOString();
      let releasedCount = 0;

      for (const queue of this.queues) {
        const collectionName = `hitl_tasks_${queue.name}`;
        const baseUrl = `${process.env.INTERNAL_API_URL}/documents/${collectionName}`;
        const queryUrl = `${baseUrl}?status=assigned`;

        try {
          // Find assigned tasks
          const response = await fetch(queryUrl, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) continue;

          const data = await response.json();
          if (!data.documents) continue;

          // Filter expired assigned tasks OR tasks with null expiration
          const expiredTasks = data.documents.filter((doc: any) => {
            const status = doc.status;
            const expiresAt = doc.expiresAt;

            // Release tasks that are:
            // 1. Assigned AND have no expiration time (null/undefined)
            // 2. Assigned AND have expired
            if (status === "assigned") {
              if (!expiresAt) {
                console.log(
                  `Found task with null expiration: ${doc.document_id}`,
                );
                return true;
              }
              if (expiresAt < currentTime) {
                console.log(`Found expired task: ${doc.document_id}`);
                return true;
              }
            }
            return false;
          });

          // Release expired tasks
          for (const doc of expiredTasks) {
            const taskId = doc.taskId;
            if (!taskId) continue;

            const taskDocUrl = `${baseUrl}/${taskId}`;
            const updatePayload = {
              status: "available",
              assignedTo: null,
              assignedAt: null,
              expiresAt: null,
            };

            const updateResponse = await fetch(taskDocUrl, {
              method: "PATCH",
              headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(updatePayload),
            });

            if (updateResponse.ok) {
              releasedCount++;
              const assignedTo = doc.assignedTo || "unknown";
              console.log(
                `Released expired task ${taskId} from user ${assignedTo}`,
              );
            }
          }
        } catch (error) {
          console.error(
            `Failed to check expired tasks for queue ${queue.name}:`,
            error,
          );
        }
      }

      if (releasedCount > 0) {
        console.log(`Released ${releasedCount} expired task assignments`);
        // Refresh queue counts after releasing expired tasks
        await this.updateQueueCountsWithAPI(
          this.queues,
          accessToken,
          idToken,
          databaseId,
        );
      }

      return releasedCount;
    } catch (error) {
      console.error("Failed to release expired tasks:", error);
      return 0;
    }
  }

  private parseDocumentData(documentDataField: any): any {
    if (!documentDataField) {
      console.log(
        "parseDocumentData: No document data field provided, returning default structure",
      );
      return {
        pageCount: 0,
        pages: [],
        loadingMetrics: {
          totalPreviewSize: 0,
          estimatedLoadTime: 0,
          recommendedPreload: 0,
        },
        resultBucket: "",
        resultPrefix: "",
        resultObject: "",
        labels: [],
        metadata: {},
      };
    }

    // console.log('parseDocumentData input type:', typeof documentDataField);
    // console.log('parseDocumentData input keys:', Object.keys(documentDataField || {}));
    // console.log('parseDocumentData input value:', documentDataField);

    // Check if it's stored as a stringValue (JSON string)
    if (documentDataField.stringValue) {
      try {
        const parsed = JSON.parse(documentDataField.stringValue);
        console.log("parseDocumentData stringValue result:", parsed);
        return parsed;
      } catch (error) {
        console.error(
          "Failed to parse documentData stringValue:",
          error,
          documentDataField.stringValue,
        );
        return {};
      }
    }

    // Check if it's stored as mapValue (nested fields)
    if (documentDataField.mapValue) {
      try {
        const fields =
          documentDataField.mapValue.fields || documentDataField.mapValue;
        const parsed = this.convertFirestoreMapToObject(fields);
        console.log("parseDocumentData mapValue result:", parsed);
        return parsed;
      } catch (error) {
        console.error(
          "Failed to parse documentData mapValue:",
          error,
          documentDataField.mapValue,
        );
        return {};
      }
    }

    // Check if it has fields directly (REST API response format)
    if (documentDataField.fields) {
      try {
        const parsed = this.convertFirestoreMapToObject(
          documentDataField.fields,
        );
        console.log("parseDocumentData fields result:", parsed);
        return parsed;
      } catch (error) {
        console.error(
          "Failed to parse documentData fields:",
          error,
          documentDataField,
        );
        return {};
      }
    }

    // Fallback: treat it as a direct object (this might be the case)
    if (typeof documentDataField === "object" && documentDataField !== null) {
      console.log("parseDocumentData direct object result:", documentDataField);
      return documentDataField;
    }

    console.warn(
      "parseDocumentData: Unknown field format:",
      typeof documentDataField,
      documentDataField,
    );
    return {};
  }

  private convertFirestoreMapToObject(fields: any): any {
    const result: any = {};

    // Safety check for undefined/null fields
    if (!fields || typeof fields !== "object") {
      console.warn(
        "convertFirestoreMapToObject received invalid fields:",
        fields,
      );
      return result;
    }

    for (const [key, value] of Object.entries(fields)) {
      // Skip null/undefined values, but allow objects with null values
      if (value === null || value === undefined) {
        result[key] = null;
        continue;
      }

      if (typeof value !== "object") {
        console.warn(`Skipping non-object field ${key}:`, value);
        result[key] = value; // Preserve primitive values as-is
        continue;
      }

      if (value.hasOwnProperty("stringValue")) {
        result[key] = value.stringValue;
      } else if (value.hasOwnProperty("integerValue")) {
        result[key] = parseInt(value.integerValue);
      } else if (value.hasOwnProperty("doubleValue")) {
        result[key] = parseFloat(value.doubleValue);
      } else if (value.hasOwnProperty("booleanValue")) {
        result[key] = value.booleanValue;
      } else if (value.hasOwnProperty("timestampValue")) {
        result[key] = new Date(value.timestampValue);
      } else if (value.hasOwnProperty("arrayValue")) {
        result[key] =
          value.arrayValue?.values?.map((v: any) =>
            this.convertFirestoreValueToJS(v),
          ) || [];
      } else if (value.hasOwnProperty("mapValue")) {
        result[key] = this.convertFirestoreMapToObject(
          value.mapValue?.fields || {},
        );
      } else if (value.hasOwnProperty("nullValue")) {
        result[key] = null;
      } else if (value.hasOwnProperty("geoPointValue")) {
        result[key] = {
          latitude: value.geoPointValue.latitude,
          longitude: value.geoPointValue.longitude,
        };
      } else if (value.hasOwnProperty("referenceValue")) {
        result[key] = value.referenceValue;
      } else if (value.hasOwnProperty("bytesValue")) {
        result[key] = value.bytesValue;
      } else {
        console.warn(
          `Unknown field type for ${key}:`,
          Object.keys(value),
          value,
        );
        // Instead of skipping unknown fields, try to preserve them as-is
        result[key] = value;
      }
    }

    return result;
  }

  private convertFirestoreValueToJS(value: any): any {
    // Handle null/undefined values
    if (value === null || value === undefined) return null;

    // Handle primitive values that aren't wrapped in Firestore format
    if (typeof value !== "object") return value;

    if (value.hasOwnProperty("stringValue")) return value.stringValue;
    if (value.hasOwnProperty("integerValue"))
      return parseInt(value.integerValue);
    if (value.hasOwnProperty("doubleValue"))
      return parseFloat(value.doubleValue);
    if (value.hasOwnProperty("booleanValue")) return value.booleanValue;
    if (value.hasOwnProperty("timestampValue"))
      return new Date(value.timestampValue);
    if (value.hasOwnProperty("arrayValue"))
      return (
        value.arrayValue?.values?.map((v: any) =>
          this.convertFirestoreValueToJS(v),
        ) || []
      );
    if (value.hasOwnProperty("mapValue"))
      return this.convertFirestoreMapToObject(value.mapValue?.fields || {});
    if (value.hasOwnProperty("nullValue")) return null;
    if (value.hasOwnProperty("geoPointValue"))
      return {
        latitude: value.geoPointValue.latitude,
        longitude: value.geoPointValue.longitude,
      };
    if (value.hasOwnProperty("referenceValue")) return value.referenceValue;
    if (value.hasOwnProperty("bytesValue")) return value.bytesValue;

    // If none of the known types match, return the value as-is
    console.warn("Unknown Firestore value type:", Object.keys(value), value);
    return value;
  }

  private getCurrentUserId(): string {
    // Get the current user ID from the app component
    const hitlApp = document.querySelector("hitl-app") as any;
    if (hitlApp && hitlApp.userId) {
      return hitlApp.userId;
    }

    // Fallback to session-based ID if no user ID is available
    if (!this.sessionUserId) {
      this.sessionUserId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      console.warn(
        `No userId found in getCurrentUserId, using session ID: ${this.sessionUserId}`,
      );
    }
    return this.sessionUserId;
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up Firestore subscriptions
    this.firestoreUnsubscribers.forEach((unsubscribe) => unsubscribe());
    this.firestoreUnsubscribers.clear();
  }

  private detectEnvironment(): string {
    // First try to detect from the current URL
    const hostname = window.location.hostname;
    console.log(`Detecting environment from hostname: ${hostname}`);

    // URL pattern: hitl-{env}.{domain} (e.g., hitl-dev-1.dev.devk.document-processing.app)
    // ALL environments have the pattern: {env}-{number} (dev-1, stg-1, prd-1, ci-1, sit-1)
    const urlMatch = hostname.match(/hitl-([^.]+)\./);

    if (urlMatch) {
      let environment = urlMatch[1]; // e.g., "dev-1", "ci-1", "stg-1", "prd-1"

      // Convert short form to full environment name with configurable client prefix
      // All environments follow the pattern: {env}-{number}
      if (environment.match(/^[a-z]+-\d+$/)) {
        environment = `${this.clientPrefix}-${environment}`; // Convert "dev-1" to "{client}-dev-1"
        console.log(
          `Auto-detected environment '${environment}' from URL: ${hostname} (client prefix: ${this.clientPrefix})`,
        );
        return environment;
      }
    }

    // Try to detect from AD redirect URI if available
    const adRedirectUri = process.env.AD_REDIRECT_URI;
    console.log(`AD redirect URI: ${adRedirectUri}`);
    if (adRedirectUri) {
      const adMatch = adRedirectUri.match(/hitl-([^.]+)\./);
      if (adMatch) {
        let environment = adMatch[1];
        // All environments follow the pattern: {env}-{number}
        if (environment.match(/^[a-z]+-\d+$/)) {
          environment = `${this.clientPrefix}-${environment}`;
          console.log(
            `Auto-detected environment '${environment}' from AD redirect URI (client prefix: ${this.clientPrefix})`,
          );
          return environment;
        }
      }
    }

    console.error(
      `Could not detect environment from URL '${hostname}'. No fallback will be used.`,
    );
    return "";
  }

  override render() {
    return html`
      <link
        href="https://unpkg.com/material-components-web@latest/dist/material-components-web.min.css"
        rel="stylesheet"
      />
      <div class="mdc-card">
        <div class="card-header">
          <div class="card-title">
            <h2 class="mdc-typography--headline6">
              Arbeitsvorrat nach Zuständigkeitsbereich
            </h2>
            <p
              class="mdc-typography--body2"
              style="margin: 4px 0; color: #666;"
            >
              Environment: <strong>${this.environment}</strong>
            </p>
          </div>
        </div>
        <div class="card-body mdc-typography--body2">
          <mwc-list multi id="checklist" @selected=${this.updateQueueSelection}>
            <mwc-linear-progress
              ?indeterminate=${true}
              ?closed=${!this.loading}
            ></mwc-linear-progress>
            <li divider role="separator"></li>
            ${this.queues.map((queue) => {
              const effectiveCount = this.getEffectiveQueueCount(
                queue,
                "available",
              );
              const displayCount =
                queue.hasMoreDocuments && effectiveCount >= 1000
                  ? ">1000"
                  : effectiveCount;
              return html`
                <mwc-check-list-item left hasMeta>
                  <span>${queue.displayName}</span>
                  <span slot="meta">${displayCount}</span>
                </mwc-check-list-item>
                <li divider role="separator"></li>
              `;
            })}
          </mwc-list>
        </div>
        <div class="mdc-card__actions">
          <mwc-textfield
            style="width: 50ex;"
            type="text"
            value="${this.envelopeIdSearch}"
            @input=${this.updateEnvelopeIdSearch}
            label="Suche nach Sendungs-ID (max. 10)"
          ></mwc-textfield>
          ${this.envelopeIdSearch
            ? html`
                <mwc-button
                  outlined
                  @click="${this.clearEnvelopeIdSearch}"
                  label="×"
                  style="min-width: 40px; padding: 0 8px;"
                ></mwc-button>
              `
            : null}
          ${this.queueType === QueueProviders.File
            ? html` <mwc-button
                outlined
                @click="${this.addFileQueue}"
                label="Zum Arbeitsstapel hinzufügen"
              ></mwc-button>`
            : null}
          <mwc-textfield
            ?disabled=${this.queues.length === 0}
            style="width: 25ex;"
            type="number"
            required
            value="10"
            @change=${this.updateReviewRequestsPerQueue}
            min="1"
            label="Angeforderte Dokumente"
          ></mwc-textfield>
          <mwc-button
            raised
            ?disabled=${this.queues.length === 0 || this.loading}
            @click="${this.addReviewRequestsToBatch}"
            label="Zum Arbeitsstapel hinzufügen"
          ></mwc-button>
        </div>
      </div>
    `;
  }

  // Optimized update method with change detection
  private requestUpdateIfChanged(): void {
    if (this.pendingUpdateRequest) return;

    // Create hash of current queue counts for change detection
    const currentHash = this.queues
      .map(
        (q) =>
          `${q.name}:${q.reviewRequestCount}:${q.assignedReviewRequestCount}`,
      )
      .join("|");

    if (currentHash !== this.lastQueueCountsHash) {
      this.lastQueueCountsHash = currentHash;
      this.pendingUpdateRequest = true;

      // Use requestIdleCallback for better performance if available
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => {
          this.requestUpdate();
          this.pendingUpdateRequest = false;
        });
      } else {
        // Fallback to immediate update
        this.requestUpdate();
        this.pendingUpdateRequest = false;
      }
    }
  }

  override async firstUpdated() {
    if (this.queueType === QueueProviders.Firestore) {
      this.loading = true;
      try {
        this.queues = await this.getFirestoreQueues();

        // Restore queue selection from session
        const sessionData = SessionManager.loadSession();
        if (sessionData && sessionData.selectedQueue) {
          try {
            this.queueSelection = JSON.parse(sessionData.selectedQueue);
          } catch (error) {
            this.queueSelection = [];
          }
        } else {
          this.queueSelection = [];
        }

        // Start real-time subscription to queue updates
        await this.subscribeToFirestoreQueues();

        const reviewRequestUpdatedEvent = new CustomEvent("queues-updated", {
          bubbles: true,
          detail: this.queues,
        });
        this.dispatchEvent(reviewRequestUpdatedEvent);
      } catch (error) {
        log.error({
          error,
          message: "Error fetching queues during first update",
          queueType: this.queueType,
          queueSelection: this.queueSelection,
          queues: this.queues,
        });
      } finally {
        this.loading = false;
      }

      // Set up periodic cleanup and unload handlers
      this.startPeriodicCleanup();
      this.setupUnloadHandler();
    }
  }

  private cleanupInterval?: number;

  private startPeriodicCleanup(): void {
    // Clean up expired tasks every 5 minutes
    this.cleanupInterval = window.setInterval(
      () => {
        this.releaseExpiredTasks();
      },
      5 * 60 * 1000,
    );
  }

  private setupUnloadHandler(): void {
    const userId = this.getCurrentUserId();

    // Release user's tasks when they leave the page
    const handleUnload = () => {
      // Use sendBeacon for more reliable cleanup on page unload
      if (navigator.sendBeacon) {
        // Note: This would need a backend endpoint to handle the release
        console.log(
          `Page unload detected, would release tasks for user ${userId}`,
        );
      } else {
        // Fallback for browsers without sendBeacon
        this.releaseUserTasks(userId);
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("pagehide", handleUnload);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();

    // Clean up intervals
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }

    // Release user's assigned tasks when component is destroyed
    const userId = this.getCurrentUserId();
    this.releaseUserTasks(userId);
  }

  updateQueueSelection = (e: CustomEvent) => {
    // Record user activity for adaptive polling
    this.recordUserActivity();

    if (e.target) {
      // @ts-ignore
      this.queueSelection = Array.from(e.target.index);

      // Persist queue selection to session
      SessionManager.updateSession({
        selectedQueue: JSON.stringify(this.queueSelection),
      });
    }
  };

  updateReviewRequestsPerQueue = (e: Event) => {
    // Record user activity for adaptive polling
    this.recordUserActivity();

    const reviewRequestsPerQueue = (e.target as HTMLTextAreaElement).value;
    this.reviewRequestsPerQueue = parseInt(reviewRequestsPerQueue);
  };

  updateEnvelopeIdSearch = (e: Event) => {
    // Record user activity for adaptive polling
    this.recordUserActivity();

    const envelopeId = (e.target as HTMLTextAreaElement).value;
    this.envelopeIdSearch = envelopeId.trim();
    console.log(`Envelope ID search updated: "${this.envelopeIdSearch}"`);
  };

  clearEnvelopeIdSearch = () => {
    // Record user activity for adaptive polling
    this.recordUserActivity();

    this.envelopeIdSearch = "";
    console.log("Envelope ID search cleared");
    this.requestUpdate();
  };

  async addFileQueue() {
    this.loading = true;
    // ensure that the access token is available (only refresh if needed)
    await this.getAccessToken(false);
    try {
      const fileQueue = await FileQueue.build(this);
      this.queues = this.queues.concat(fileQueue);

      const reviewRequestUpdatedEvent = new CustomEvent("queues-updated", {
        bubbles: true,
        detail: this.queues,
      });
      this.dispatchEvent(reviewRequestUpdatedEvent);
    } catch {
      console.log("fetching file queues failed");
    }
    this.loading = false;
  }

  async addReviewRequestsToBatch() {
    // Record user activity for adaptive polling
    this.recordUserActivity();

    // Debouncing: if already loading, return the existing promise
    if (this.loadingPromise || this.loading) {
      console.log("Batch loading already in progress, ignoring request...");
      return;
    }

    // Immediately set loading state to prevent multiple clicks
    this.loading = true;
    this.requestUpdate(); // Force UI update to disable button

    // Create and store the loading promise
    this.loadingPromise = this._performBatchLoad();

    try {
      await this.loadingPromise;
    } finally {
      // Clear the promise when done (whether successful or not)
      this.loadingPromise = null;
      this.loading = false;
      this.requestUpdate(); // Force UI update to re-enable button
    }
  }

  private async _performBatchLoad() {
    // Immediate feedback: dispatch empty batch to show UI is responding
    const immediateEvent = new CustomEvent("review-requests-loading", {
      bubbles: true,
      detail: { queuesCount: this.queueSelection.length },
    });
    this.dispatchEvent(immediateEvent);

    try {
      // ensure that the access token is available (only refresh if needed)
      await this.getAccessToken(false);
      const selectedQueus = this.queueSelection.map(
        (index) => this.queues[index],
      );

      // Process queues in parallel for better performance
      const CHUNK_SIZE = 2; // Process documents in chunks of 2 for immediate visibility
      const allReviewRequests: any[] = [];

      // Fetch from all queues in parallel
      const queueResults = await Promise.all(
        selectedQueus.map(async (queue, index) => {
          // Dispatch progress update
          const progressEvent = new CustomEvent("review-requests-progress", {
            bubbles: true,
            detail: {
              current: index + 1,
              total: selectedQueus.length,
              queueName: queue.name,
            },
          });
          this.dispatchEvent(progressEvent);

          try {
            // Skip queues with no available tasks AND no assigned tasks
            if (
              queue.reviewRequestCount === 0 &&
              (queue.assignedCount === 0 || queue.assignedCount === undefined)
            ) {
              console.log(`Skipping empty queue: ${queue.name}`);
              return [];
            }

            const requests = await queue.getReviewRequests(
              this.reviewRequestsPerQueue,
            );

            // Note: Local adjustments are applied in fetchAndAssignFirestoreTasksREST to avoid double counting
            return requests;
          } catch (error) {
            console.warn(
              `Failed to fetch requests from queue ${queue.name}:`,
              error,
            );
            return []; // Return empty array for failed queues
          }
        }),
      );

      // Flatten results
      const batchReviewRequests = queueResults.flat();

      // Note: Local adjustments are now applied in getFirestoreReviewRequests per queue
      if (batchReviewRequests.length > 0) {
        console.log(
          `📄 ${batchReviewRequests.length} documents loaded into work stack`,
        );
      }

      // Progressive loading: dispatch documents in chunks
      for (let i = 0; i < batchReviewRequests.length; i += CHUNK_SIZE) {
        const chunk = batchReviewRequests.slice(i, i + CHUNK_SIZE);
        allReviewRequests.push(...chunk);

        // Dispatch only the new chunk to avoid duplicates
        const reviewRequestUpdatedEvent = new CustomEvent(
          "review-requests-updated",
          {
            bubbles: true,
            detail: chunk, // Send only the new chunk
          },
        );
        this.dispatchEvent(reviewRequestUpdatedEvent);

        // Small delay to allow UI to update between chunks
        if (i + CHUNK_SIZE < batchReviewRequests.length) {
          await new Promise((resolve) => setTimeout(resolve, 20));
        }
      }

      // Dispatch completion event to signal loading is done
      const completedEvent = new CustomEvent("review-requests-completed", {
        bubbles: true,
        detail: { count: allReviewRequests.length },
      });
      this.dispatchEvent(completedEvent);
    } catch (error) {
      console.error("Error in addReviewRequestsToBatch:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const errorEvent = new CustomEvent("review-requests-error", {
        bubbles: true,
        detail: { error: errorMessage },
      });
      this.dispatchEvent(errorEvent);
      throw error; // Re-throw to handle in the main method
    }
  }

  // Cache access token to avoid repeated refreshes
  private cachedAccessToken: string | null = null;
  private tokenCacheExpiry: number = 0;
  private static readonly TOKEN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  async getAccessToken(refresh = false) {
    // Check if we have a valid cached token and refresh is not forced
    const now = Date.now();
    if (!refresh && this.cachedAccessToken && now < this.tokenCacheExpiry) {
      this.updateQueueTokens(this.cachedAccessToken);
      return [this.cachedAccessToken, this.cachedIdToken];
    }

    const [accessToken, idToken] = await new Promise<[string, string]>(
      (resolve, reject) => {
        const getAccessToken = new CustomEvent("get-access-token", {
          bubbles: true,
          detail: { refresh: refresh, resolve: resolve, reject: reject },
        });
        this.dispatchEvent(getAccessToken);
      },
    );

    // Cache the token
    this.cachedAccessToken = accessToken;
    this.cachedIdToken = idToken;
    this.tokenCacheExpiry = now + Queues.TOKEN_CACHE_DURATION;

    this.updateQueueTokens(accessToken);
    return [accessToken, idToken];
  }

  private updateQueueTokens(accessToken: string) {
    this.queues.forEach((queue) => {
      if (queue instanceof PubSubQueue) {
        queue.accessToken = accessToken;
      }
    });
  }

  async getPubSubQueues(): Promise<PubSubQueue[]> {
    // Must have environment to proceed
    if (!this.environment) {
      console.error(
        "No environment detected. Cannot fetch HITL subscriptions.",
      );
      return [];
    }

    const now = Date.now();

    // Check cache first
    if (
      Queue.subscriptionCache &&
      now - Queue.subscriptionCache.timestamp < Queue.CACHE_TTL
    ) {
      console.log("Using cached subscription data");
      // Efficiently update access tokens for cached queues
      const [accessToken] = await this.getAccessToken();
      Queue.subscriptionCache.data.forEach(
        (queue) => (queue.accessToken = accessToken),
      );
      return Queue.subscriptionCache.data;
    }

    console.log("Fetching fresh subscription data");

    try {
      // Get access token once
      const [accessToken] = await this.getAccessToken();

      // Direct API call to get subscriptions for the specific HITL topic
      const hitlTopicName = `${this.environment}-hitl`;
      const topicPath = `projects/${this.projectId}/topics/${hitlTopicName}`;

      console.log(`Fetching subscriptions for HITL topic: ${hitlTopicName}`);

      const response = await fetch(
        `https://pubsub.googleapis.com/v1/${topicPath}/subscriptions`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch subscriptions for topic ${hitlTopicName}: ${response.status} ${response.statusText}`,
        );
      }

      const { subscriptions = [] } = await response.json();

      console.log(
        `Found ${subscriptions.length} HITL subscriptions for topic ${hitlTopicName}`,
      );

      // Create queue instances efficiently
      const pubSubQueues = subscriptions.map(
        (subscription: string) =>
          new PubSubQueue(this, accessToken, subscription),
      );

      // Cache the results
      Queue.subscriptionCache = {
        data: pubSubQueues,
        timestamp: now,
      };

      return pubSubQueues;
    } catch (error) {
      console.error("Failed to fetch HITL subscriptions:", error);
      return [];
    }
  }

  private inferEnvironmentFromQueues(queues: PubSubQueue[]): string {
    // Look at queue names to find common environment pattern
    const environments = new Set<string>();

    queues.forEach((queue) => {
      // Use configurable client prefix instead of hardcoded "devk"
      const clientPrefix = this.clientPrefix || "devk";
      // All environments follow the pattern: {client}-{env}-{number} where {env}-{number} is like dev-1, stg-1, prd-1
      const match = queue.name.match(
        new RegExp(`^(${clientPrefix}-[a-z]+-\\d+)`),
      );
      if (match) {
        environments.add(match[1]);
      }
    });

    // If we found exactly one environment, use it
    if (environments.size === 1) {
      return Array.from(environments)[0];
    }

    // If multiple environments, log warning and return empty
    if (environments.size > 1) {
      console.warn(
        `Multiple environments found in queues: ${Array.from(environments).join(", ")}`,
      );
    }

    return "";
  }

  private async fetchAllTopics(accessToken: string): Promise<any[]> {
    let topics: any[] = [];
    let pageToken: string | undefined;

    do {
      try {
        let url = `https://pubsub.googleapis.com/v1/projects/${this.projectId}/topics`;
        if (pageToken) {
          url += `?pageToken=${pageToken}`;
        }

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (!response.ok) {
          throw response;
        }

        const json = await response.json();
        const allTopics = json.topics || [];

        // Log all topics for debugging
        console.log(
          `All topics found (page):`,
          allTopics.map((t: any) => t.name.split("/").pop()),
        );
        console.log(`Current environment for filtering: '${this.environment}'`);

        // Filter topics by environment to reduce API calls
        const environmentTopics = allTopics.filter((topic: any) => {
          const topicName = topic.name.split("/").pop(); // Get topic name from full path

          // Skip filtering if no environment detected
          if (!this.environment) {
            console.warn("No environment detected, returning all topics");
            return true;
          }

          // Check multiple naming patterns:
          // 1. Exact prefix: devk-dev-1-hitl-*
          // 2. With number suffix: devk-dev-1-hitl-*, devk-dev-2-hitl-*, devk-dev-3-hitl-*
          // 3. Contains environment: *devk-dev*hitl*
          const envBase = this.environment.replace(/-\d+$/, ""); // Remove number suffix if present
          const matches =
            topicName?.startsWith(this.environment) ||
            topicName?.startsWith(`${envBase}-`) ||
            (topicName?.includes(envBase) && topicName?.includes("hitl"));

          if (matches) {
            console.log(
              `Topic ${topicName} matches environment ${this.environment}`,
            );
          }
          return matches;
        });
        topics = topics.concat(environmentTopics);
        pageToken = json.nextPageToken;
      } catch (error) {
        console.log("Failed on fetching topics", error);
        break;
      }
    } while (pageToken);

    console.log(
      `Filtered topics for environment ${this.environment}:`,
      topics.length,
    );

    // Fallback: if no environment-specific topics found, fetch all topics with warning
    if (topics.length === 0) {
      console.warn(
        `No topics found for environment ${this.environment}. Falling back to all topics for debugging.`,
      );
      // Re-fetch without filtering for debugging
      try {
        const response = await fetch(
          `https://pubsub.googleapis.com/v1/projects/${this.projectId}/topics`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        );
        const json = await response.json();
        const allTopics = json.topics || [];
        console.log(
          `All available topics:`,
          allTopics.map((t: any) => t.name.split("/").pop()),
        );
        return allTopics; // Return all topics as fallback
      } catch (error) {
        console.error("Failed to fetch fallback topics:", error);
      }
    }

    return topics;
  }

  private async batchFetchSubscriptions(
    topics: any[],
    accessToken: string,
  ): Promise<string[]> {
    // Batch fetch subscriptions for all topics using Promise.allSettled
    const subscriptionPromises = topics.map(async (topic: any) => {
      try {
        const response = await fetch(
          `https://pubsub.googleapis.com/v1/${topic.name}/subscriptions`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch subscriptions for topic ${topic.name}`,
          );
        }

        const json = await response.json();
        return json.subscriptions || [];
      } catch (error) {
        console.log(
          `Failed on fetching subscriptions for topic ${topic.name}:`,
          error,
        );
        return [];
      }
    });

    const results = await Promise.allSettled(subscriptionPromises);

    // Extract successful results and flatten
    const allSubscriptions = results
      .filter(
        (result): result is PromiseFulfilledResult<string[]> =>
          result.status === "fulfilled",
      )
      .map((result) => result.value)
      .flat()
      .filter(Boolean);

    return allSubscriptions;
  }

  private async batchFetchHitlSubscriptions(
    subscriptions: string[],
    accessToken: string,
  ): Promise<string[]> {
    // Pre-filter subscriptions by environment and hitl naming convention to reduce API calls
    const environmentSubscriptions = subscriptions.filter((subscription) => {
      const subscriptionName = subscription.split("/").pop(); // Get subscription name from full path

      // Skip filtering if no environment detected
      if (!this.environment) {
        return subscriptionName?.includes("hitl");
      }

      // Check multiple naming patterns similar to topic filtering
      const envBase = this.environment.replace(/-\d+$/, ""); // Remove number suffix if present
      return (
        (subscriptionName?.includes(this.environment) ||
          subscriptionName?.includes(envBase)) &&
        subscriptionName?.includes("hitl")
      );
    });

    console.log(
      `Pre-filtered subscriptions for environment ${this.environment}:`,
      environmentSubscriptions.length,
      "out of",
      subscriptions.length,
    );

    // Batch fetch subscription details using Promise.allSettled
    const hitlPromises = environmentSubscriptions.map(
      async (subscription: string) => {
        try {
          const response = await fetch(
            `https://pubsub.googleapis.com/v1/${subscription}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            },
          );

          if (!response.ok) {
            throw new Error(
              `Failed to fetch subscription details for ${subscription}`,
            );
          }

          const json = await response.json();

          // Return subscription name if it has hitl=true label
          if (json.labels?.hitl === "true") {
            return subscription;
          }
          return null;
        } catch (error) {
          console.log(
            `Failed on fetching subscription details for ${subscription}:`,
            error,
          );
          return null;
        }
      },
    );

    const results = await Promise.allSettled(hitlPromises);

    // Extract successful results and filter out nulls
    const hitlSubscriptions = results
      .filter(
        (result): result is PromiseFulfilledResult<string | null> =>
          result.status === "fulfilled",
      )
      .map((result) => result.value)
      .filter(Boolean) as string[];

    return hitlSubscriptions;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "hitl-queues": Queues;
  }
}

// Cache interface for subscription discovery
interface SubscriptionCache {
  data: PubSubQueue[];
  timestamp: number;
}

export abstract class Queue implements ReactiveController {
  host: ReactiveControllerHost;
  name: string = "";
  reviewRequestCount: number = 0;
  protected _timerID?: number;
  static subscriptionCache: SubscriptionCache | null = null;
  static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(host: ReactiveControllerHost, name: string) {
    (this.host = host).addController(this);
    this.name = name;
  }

  abstract getReviewRequestCount(): Promise<void>;

  abstract getReviewRequests(count: number): Promise<ReviewRequest[]>;

  hostConnected() {
    // Start a timer when the host is connected with adaptive interval
    this._timerID = window.setInterval(
      () => this.getReviewRequestCount(),
      this.getRefreshInterval(),
    );
  }

  protected getRefreshInterval(): number {
    // More frequent refresh for queues with messages, less frequent for empty ones
    return this.reviewRequestCount > 0 ? 60 * 1000 : 300 * 1000; // 1min vs 5min
  }

  hostDisconnected() {
    // Clear the timer when the host is disconnected
    clearInterval(this._timerID);
    this._timerID = undefined;
  }
}

class FileQueue extends Queue {
  fileSystemDirectoryHandle: FileSystemDirectoryHandle;

  constructor(
    host: ReactiveControllerHost,
    fileSystemDirectoryHandle: FileSystemDirectoryHandle,
  ) {
    if (typeof fileSystemDirectoryHandle === "undefined") {
      throw new Error("Cannot be called directly");
    }
    super(host, fileSystemDirectoryHandle.name);
    this.fileSystemDirectoryHandle = fileSystemDirectoryHandle;
    this.getReviewRequestCount();
  }

  static async build(host: ReactiveControllerHost) {
    const fileSystemDirectoryHandle = await window.showDirectoryPicker();
    return new FileQueue(host, fileSystemDirectoryHandle);
  }

  getReviewRequests = async (count: number): Promise<ReviewRequest[]> => {
    const reviewRequests: ReviewRequest[] = [];
    const entries = this.fileSystemDirectoryHandle.entries();
    let i = 0;
    for await (const entry of entries) {
      if (
        entry[0].endsWith(".json") &&
        !entry[0].endsWith(".grouping-labels.json") &&
        !entry[0].endsWith(".entity-labels.json") &&
        i < count
      ) {
        const name = entry[0];
        const reviewRequestFileSystemFileHandle =
          await this.fileSystemDirectoryHandle.getFileHandle(name);
        const reviewRequestFile =
          await reviewRequestFileSystemFileHandle.getFile();
        const lastModified = new Date(reviewRequestFile.lastModified);
        const fileBaseName = reviewRequestFile.name.replace(".json", "");
        const groupingLabelsFileName = fileBaseName + ".grouping-labels.json";
        const classificationLabelsFileName =
          fileBaseName + ".classification-labels.json";
        const entityLabelsFileName = fileBaseName + ".entity-labels.json";
        let type: ReviewRequestType = "Entity";
        let labels: EntityLabel[] | ClassificationLabel[] | GroupingLabel[] =
          [];
        let pageImageUrls: string[] = [];
        for await (const [
          fileName,
          fileSystemFileHandle,
        ] of this.fileSystemDirectoryHandle.entries()) {
          if (groupingLabelsFileName === fileName) {
            labels = await this.getGroupingLabels(
              fileSystemFileHandle as FileSystemFileHandle,
            );
            type = "DocSplitter";
          } else if (classificationLabelsFileName === fileName) {
            labels = await this.getClassificationLabels(
              fileSystemFileHandle as FileSystemFileHandle,
            );
            type = "DAI_COMPLEX_CLASSIFICATION";
          } else if (entityLabelsFileName === fileName) {
            labels = await this.getEntityLabels(
              fileSystemFileHandle as FileSystemFileHandle,
            );
          } else if (
            // if file ends with png or jpg then use the images as pageImageUrls
            (fileName.endsWith(".png") || fileName.endsWith(".jpg")) &&
            fileName.startsWith(fileBaseName)
          ) {
            const file = await (
              fileSystemFileHandle as FileSystemFileHandle
            ).getFile();
            // for File based images we need to convert image data to Data URL for Images served via HTTP the URL can directly be used
            const pageImageUrl = await this.toDataUrl(file);
            const pageString = fileName
              .replace(fileBaseName, "")
              .replace(".page-", "")
              .replace(".png", "")
              .replace(".jpg", "");
            const page = parseInt(pageString);
            pageImageUrls[page - 1] = pageImageUrl;
          }
        }
        // initialize file based review request - a different class implemeting ReviewRequest should be used for a different backend
        const reviewRequest = new FileReviewRequest(
          reviewRequestFileSystemFileHandle,
          type,
          reviewRequestFile.name,
          lastModified,
          this.name,
          labels,
          pageImageUrls,
        );
        // add review request to list of review requests
        reviewRequests.push(reviewRequest);
        i++;
      }
    }
    return reviewRequests;
  };

  toDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  async getEntityLabels(
    entityLabelsFileHandle: FileSystemFileHandle,
  ): Promise<EntityLabel[]> {
    let entityLabels: EntityLabel[] = [];
    try {
      const file = await entityLabelsFileHandle.getFile();
      const content = await file.text();
      const json = JSON.parse(content);
      if (Array.isArray(json)) {
        entityLabels = json.map((entityLabel) => {
          return EntityLabel.fromJson(entityLabel);
        });
      }
    } catch {
      console.log("retrieving entity labels failed");
    }
    return entityLabels;
  }

  async getGroupingLabels(
    groupingLabelsFileHandle: FileSystemFileHandle,
  ): Promise<GroupingLabel[]> {
    let groupingLabels: GroupingLabel[] = [];
    try {
      const file = await groupingLabelsFileHandle.getFile();
      const content = await file.text();
      const json = JSON.parse(content);
      if (Array.isArray(json)) {
        groupingLabels = json.map((groupingLabel) => {
          return GroupingLabel.fromJson(groupingLabel);
        });
      }
    } catch {
      console.log("retrieving entity labels failed");
    }
    return groupingLabels;
  }

  // get classification labels from classification-labels.json file
  async getClassificationLabels(
    classificationLabelsFileHandle: FileSystemFileHandle,
  ): Promise<ClassificationLabel[]> {
    let classificationLabels: ClassificationLabel[] = [];
    try {
      const file = await classificationLabelsFileHandle.getFile();
      const content = await file.text();
      const json = JSON.parse(content);
      if (Array.isArray(json)) {
        classificationLabels = json.map((classificationLabel) => {
          return ClassificationLabel.fromJson(classificationLabel);
        });
      }
    } catch {
      console.log("retrieving classification labels failed");
    }
    return classificationLabels;
  }

  async getReviewRequestCount(): Promise<void> {
    let count = 0;
    const entries = this.fileSystemDirectoryHandle.entries();
    for await (const entry of entries) {
      if (
        entry[0].endsWith(".json") &&
        !entry[0].endsWith(".grouping-labels.json") &&
        !entry[0].endsWith(".classification-labels.json") &&
        !entry[0].endsWith(".entity-labels.json")
      ) {
        count++;
      }
    }
    // get reviewRequestCount every 60 seconds
    this.reviewRequestCount = count;
    // Update the host with new value
    this.host.requestUpdate();
  }
}

class PubSubQueue extends Queue {
  subscriptionName: string;
  subscriptionId: string;
  projectId: string;
  accessToken: string;

  constructor(
    host: ReactiveControllerHost,
    accessToken: string,
    subscriptionName: string,
  ) {
    const subscriptionId = subscriptionName.split("/")[3];
    const displayName = getQueueDisplayName(subscriptionId);
    super(host, displayName);
    this.subscriptionId = subscriptionId;
    this.projectId = subscriptionName.split("/")[1];
    this.accessToken = accessToken;
    this.subscriptionName = subscriptionName;
    this.getReviewRequestCount();
  }

  async getReviewRequestCount(): Promise<void> {
    let count = this.reviewRequestCount;
    try {
      const response = await fetch(
        "https://monitoring.googleapis.com/v3/projects/" +
          this.projectId +
          "/timeSeries:query",
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + this.accessToken,
          },
          body: JSON.stringify({
            query: `fetch pubsub_subscription | metric 'pubsub.googleapis.com/subscription/num_undelivered_messages' | filter (resource.subscription_id == '${this.subscriptionId}') | within 2m`,
          }),
        },
      );
      if (!response.ok) {
        throw response;
      }
      const json = await response.json();
      //  undelivered messages, if null then return previous value
      count =
        json.timeSeriesData
          ?.pop()
          ?.pointData?.find(Boolean)
          ?.values?.find(Boolean)?.int64Value ?? this.reviewRequestCount;
    } catch (error) {
      console.log("Failed on quering monitoring");
      console.log(error);
    }
    this.reviewRequestCount = count;
    // Update the host with new value
    this.host.requestUpdate();
  }

  override getReviewRequests = async (
    count: number,
  ): Promise<ReviewRequest[]> => {
    const reviewRequests: ReviewRequest[] = [];
    // retrieve count messages from Pub/Sub subscription
    let url =
      "https://pubsub.googleapis.com/v1/" + this.subscriptionName + ":pull";
    try {
      const response = await fetchRetry(
        url,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this.accessToken}`,
          },
          body: JSON.stringify({
            maxMessages: count,
          }),
        },
        5, // timeout in seconds - aggressive timeout for faster responses
        1, // retries - minimal retries for faster failure detection
      );
      const json = await response.json();
      const receivedMessages = json.receivedMessages ?? [];

      // Early return if no messages to process
      if (receivedMessages.length === 0) {
        console.log(`No messages available in queue: ${this.name}`);
        return reviewRequests;
      }

      console.log(
        `Processing ${receivedMessages.length} messages from queue: ${this.name}`,
      );

      // Use Web Worker for large batches, otherwise process in main thread
      const USE_WORKER_THRESHOLD = 20;
      let processedMessages: any[];

      if (
        this.messageWorker &&
        receivedMessages.length > USE_WORKER_THRESHOLD
      ) {
        // Process in Web Worker for better performance with large batches
        processedMessages =
          await this.processMessagesInWorker(receivedMessages);
      } else {
        // Process messages with optimized operations in main thread
        processedMessages = receivedMessages
          .map((message: any) => {
            try {
              // Critical operations only - defer heavy processing
              const data = JSON.parse(window.atob(message.message.data));

              // Optimized: use centralized lookup for faster type determination
              const type: ReviewRequestType =
                PROCESSOR_TO_REVIEW_TYPE_MAP[data.processorType] || "Entity";

              // Defer label processing - create lightweight placeholder
              const labels:
                | EntityLabel[]
                | ClassificationLabel[]
                | GroupingLabel[] = [];

              // Store raw data for deferred processing
              return {
                message,
                data,
                type,
                labels,
                rawLabels: data.labels, // Store for later processing
              };
            } catch (error) {
              console.error(`Failed to process message:`, error);
              return null;
            }
          })
          .filter(Boolean); // Remove failed messages
      }

      // Process in batches for better UI responsiveness
      const BATCH_SIZE = 5;
      for (let i = 0; i < processedMessages.length; i += BATCH_SIZE) {
        const batch = processedMessages.slice(i, i + BATCH_SIZE);

        await Promise.all(
          batch.map(async ({ message, data, type, labels, rawLabels }) => {
            // Build pageImageUrls
            const storageBaseUrl = `https://storage.googleapis.com/${data.resultBucket}/`;
            const pageImageUrlPrefix = data.pageImageUrlPrefix + "/";
            const pageImageUrls = data.pageImageNames.map(
              (pageImageName: string) =>
                storageBaseUrl +
                encodeURIComponent(pageImageUrlPrefix + pageImageName),
            );

            // Process labels only when needed
            if (rawLabels?.length > 0) {
              if (type === "DocSplitter") {
                rawLabels.forEach((label: any) => {
                  const groupingLabel = GroupingLabel.fromJson(label);
                  (labels as GroupingLabel[]).push(groupingLabel);
                });
              } else if (type === "DAI_COMPLEX_CLASSIFICATION") {
                rawLabels.forEach((label: any) => {
                  const classificationLabel =
                    ClassificationLabel.fromJson(label);
                  (labels as ClassificationLabel[]).push(classificationLabel);
                });
              } else {
                rawLabels.forEach((label: any) => {
                  const entityLabel = EntityLabel.fromJson(label);
                  (labels as EntityLabel[]).push(entityLabel);
                });
              }
            }
            const reviewRequest = new PubSubReviewRequest(
              this.projectId,
              this.subscriptionName,
              message.ackId,
              message.message.messageId,
              data.resultBucket,
              data.resultPrefix,
              data.resultObject,
              data.reviewedResultBucket ?? "",
              data.rejectedResultBucket ?? "",
              data.callbackUrl,
              type,
              data.inputObject,
              new Date(message.message.publishTime),
              this.name,
              labels,
              pageImageUrls,
              data,
              data.revisions,
            );
            reviewRequests.push(reviewRequest);
          }),
        );

        // Small delay between batches to keep UI responsive
        if (i + BATCH_SIZE < processedMessages.length) {
          await new Promise((resolve) => setTimeout(resolve, 20));
        }
      }
      // remove timer for refreshing reviewRequestCount
      clearInterval(this._timerID);
      // remove the number of messages taken from the list from the overall count
      this.reviewRequestCount = Math.max(
        this.reviewRequestCount - receivedMessages.length,
        0,
      );
      // restart timer for refreshing reviewRequestCount with adaptive interval
      this._timerID = undefined;
      this._timerID = window.setInterval(
        () => this.getReviewRequestCount(),
        this.getRefreshInterval(),
      );
      // Update the host with new value
      this.host.requestUpdate();
    } catch (error) {
      console.log("Failed to fetch messages from Pub/Sub subscription");
    }
    return reviewRequests;
  };

  // Process messages in Web Worker for better performance
  async processMessagesInWorker(messages: any[]): Promise<any[]> {
    return new Promise((resolve, reject) => {
      if (!this.messageWorker) {
        reject(new Error("Web Worker not available"));
        return;
      }

      // Use centralized type map for the worker
      const typeMap = PROCESSOR_TO_REVIEW_TYPE_MAP;

      // Set up one-time message handler
      const handler = (event: MessageEvent) => {
        if (event.data.success) {
          resolve(event.data.results);
        } else {
          reject(new Error(event.data.error));
        }
        this.messageWorker!.removeEventListener("message", handler);
      };

      this.messageWorker.addEventListener("message", handler);

      // Send messages to worker for processing
      this.messageWorker.postMessage({
        messages,
        typeMap,
      });
    });
  }
}
